// src/services/supabaseClient.ts
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

// -------------------------------
// 1) RÉCUPÉRATION SÉCURISÉE DES CLÉS
// -------------------------------
/**
 * IMPORTANT SÉCURITÉ :
 * Aucune vraie clé ne doit être hardcodée ici.
 *
 * Les valeurs doivent venir de :
 * - .env en local
 * - variables d'environnement Render en production
 *
 * Côté frontend, seules les clés publiques VITE_* peuvent être exposées.
 * Les secrets comme service role, Stripe secret, OpenAI, Amadeus secret, Shippo secret
 * doivent rester côté backend / Supabase Edge Functions / Render backend.
 */
const envUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim();
const envKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim();

const supabaseUrl = envUrl || '';
const supabaseAnonKey = envKey || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    [
      '🚨 ERREUR CRITIQUE SUPABASE : configuration manquante.',
      'VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY doivent être définies dans le .env local ou dans Render.',
      'Aucune clé Supabase ne doit être hardcodée dans src/services/supabaseClient.ts.',
    ].join(' ')
  );
} else {
  // Log discret : ne jamais afficher la clé complète dans la console.
  console.log(`✅ Supabase Client initialisé sur : ${supabaseUrl}`);
}

// -------------------------------
// 2) SINGLETON SUPABASE (Anti multi-instances / HMR)
// -------------------------------
type GlobalWithSupabase = typeof globalThis & {
  __FORFEO_SUPABASE__?: SupabaseClient;
  __FORFEO_SUPABASE_META__?: {
    url?: string;
    anonKeyPresent?: boolean;
  };

  // Aliases potentiels conservés pour compatibilité avec d'anciens morceaux du projet.
  __SUPABASE__?: SupabaseClient;
  __SUPABASE_META__?: {
    url?: string;
  };
};

const g = globalThis as GlobalWithSupabase;

const isBrowser = typeof window !== 'undefined';

// Important : évite d’accéder à localStorage côté SSR/build.
const storage = isBrowser ? window.localStorage : undefined;

// On récupère une éventuelle instance existante.
const existing = g.__FORFEO_SUPABASE__ || g.__SUPABASE__;

// Vérification de cohérence utile en développement/HMR.
if (existing && supabaseUrl) {
  const knownUrl = g.__FORFEO_SUPABASE_META__?.url || g.__SUPABASE_META__?.url;

  if (knownUrl && knownUrl !== supabaseUrl) {
    console.warn(
      "⚠️ Changement de configuration Supabase détecté (HMR). L'ancienne instance est conservée pour éviter des conflits."
    );
  }
}

// -------------------------------
// 3) SAFE FETCH WRAPPER
// -------------------------------
/**
 * Intercepte les requêtes Supabase pour garantir que certains headers utiles
 * restent présents.
 *
 * Objectifs :
 * - conserver x-client-info pour les logs ;
 * - ajouter apikey si absente ;
 * - ne jamais retirer Authorization ;
 * - ne jamais modifier destructivement method/body/credentials.
 */
const forfeoFetch: typeof fetch = async (input, init) => {
  const baseReq = input instanceof Request ? input : new Request(input, init);

  const headers = new Headers(init?.headers ?? baseReq.headers);

  if (!headers.has('x-client-info')) {
    headers.set('x-client-info', 'forfeo-explore-quebec');
  }

  if (supabaseAnonKey && !headers.has('apikey')) {
    headers.set('apikey', supabaseAnonKey);
  }

  const finalReq = new Request(baseReq, {
    ...(init || {}),
    headers,
  });

  return fetch(finalReq);
};

// -------------------------------
// 4) CRÉATION DU CLIENT (OU RÉUTILISATION)
// -------------------------------
export const supabase: SupabaseClient =
  existing ??
  createClient(supabaseUrl, supabaseAnonKey, {
    global: {
      headers: {
        'x-client-info': 'forfeo-explore-quebec',
      },
      fetch: forfeoFetch,
    },
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage,

      // On laisse storageKey par défaut pour compatibilité :
      // 'sb-<project-ref>-auth-token'
    },
    db: {
      schema: 'public',
    },
  });

// -------------------------------
// 5) SAUVEGARDE DANS LE SINGLETON
// -------------------------------
if (!existing) {
  g.__FORFEO_SUPABASE__ = supabase;
  g.__FORFEO_SUPABASE_META__ = {
    url: supabaseUrl,
    anonKeyPresent: !!supabaseAnonKey,
  };

  // Fallback alias conservé pour compatibilité avec d'anciens morceaux du projet.
  g.__SUPABASE__ = supabase;
  g.__SUPABASE_META__ = {
    url: supabaseUrl,
  };
}

// -------------------------------
// 6) EXPORTS UTILES
// -------------------------------
export const SUPABASE_URL = supabaseUrl;

// Supporte les imports "default".
export default supabase;
